import CreateDeliveryModal from "@/routes/processes/_components/create-delivery-modal";
import { ResetFilters } from "@/routes/processes/_components/reset-filters";
import { useFeatureAuth } from "@/services/backend/auth/hooks/use-feature-auth";
import type { Delivery } from "@/services/backend/deliveries/delivery/delivery";
import type { ListRequest } from "@/services/backend/deliveries/delivery/service";
import { useDeliveriesListQuery } from "@/services/backend/deliveries/delivery/service";
import { State } from "@/services/backend/deliveries/state/state";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import {
  DataTable,
  DataTableContainer,
  useDataTableControllerWithSorting,
} from "@/shared/components/data-table";
import { CustomerName } from "@/shared/components/domain/customer/customer-name";
import { Button } from "@/shared/components/ui/button";
import { ColumnSelection } from "@/shared/components/ui/column-selection-component";
import { PaginationLimitOffset } from "@/shared/components/ui/pagination-limit-offset";
import { FilterMode } from "@/shared/lib/filter/filter";
import { formatDate, formatTime } from "@/shared/utilities/date";
import type { ColumnDef } from "@tanstack/react-table";
import { ArrowDownIcon, ArrowUpIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink } from "react-router";
import { UpdateDeliveryCondition } from "../../../shared/components/domain/deliveries/update-delivery-condition";
import { UpdateDeliveryState } from "../../../shared/components/domain/deliveries/update-delivery-state";
import { StateIcon } from "../../processes/_components/state-icon";
import { ConditionFilter } from "./condition-filter";
import { StateFilter } from "./state-filter";

const tableColumns = (editDelivery: boolean): ColumnDef<Delivery>[] => [
  {
    id: "description",
    accessorKey: "description",
    header: "Beschreibung",
    cell: ({ row }) => {
      const deliveryData = row.original;
      return (
        <NavLink to={`/processes/${deliveryData.processId}/delivery`}>
          <Button variant="ghost" size="sm" className="-mt-1.5 -mb-2.5">
            <span className="text-teal-600">{deliveryData.description}</span>
          </Button>
        </NavLink>
      );
    },
  },
  {
    id: "customerId",
    accessorKey: "customerId",
    header: "Kunde",
    cell: ({ row }) => {
      const deliveryData = row.original;
      return <CustomerName customerId={deliveryData.customerId} />;
    },
  },
  {
    id: "deliveryState",
    accessorKey: "deliveryState",
    header: "Lieferstatus",
    cell: ({ row }) => {
      const deliveryData = row.original;
      return (
        <StateIcon key={deliveryData.id} label={deliveryData.deliveryState} />
      );
    },
  },
  {
    id: "createdAt",
    accessorKey: "createdAt",
    header: ({ column }) => (
      <Button
        className="p-0"
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Erstellungsdatum
        {column.getIsSorted() === "asc" && (
          <ArrowUpIcon className="ml-2 h-4 w-4" />
        )}
        {column.getIsSorted() === "desc" && (
          <ArrowDownIcon className="ml-2 h-4 w-4" />
        )}
      </Button>
    ),
    cell: ({ row }) => {
      const deliveryData = row.original;
      return (
        <div className="flex flex-col">
          <span className="font-bold">
            {formatDate(deliveryData.createdAt!)}
          </span>
          <span>{formatTime(deliveryData.createdAt!)}</span>
        </div>
      );
    },
  },
  {
    id: "editDeliveryState",
    accessorKey: "deliveryState",
    header: "Lieferung bestätigen",
    cell: ({ row }) => {
      const deliveryData = row.original;
      return (
        <UpdateDeliveryState
          deliveryId={deliveryData.id}
          currentDeliveryState={deliveryData.deliveryState}
          disabled={deliveryData.immutable || !editDelivery}
        />
      );
    },
  },
  {
    id: "editCondition",
    accessorKey: "condition",
    header: "Paketzustand",
    cell: ({ row }) => {
      const deliveryData = row.original;
      return (
        <UpdateDeliveryCondition
          deliveryId={deliveryData.id}
          currentCondition={deliveryData.condition}
          disabled={deliveryData.immutable || !editDelivery}
        />
      );
    },
  },
];

export function DeliveriesListTable() {
  const canAccessState = useFeatureAuth("inspections.delivery.editState");
  const canAccessCreate = useFeatureAuth("inspections.delivery.create");
  const cols = tableColumns(canAccessState);
  const [request, setRequest] = useState<ListRequest>(defaultRequest());
  const {
    data: list,
    isLoading,
    isFetching,
    error,
  } = useDeliveriesListQuery(request, {
    refetchOnMountOrArgChange: true,
  });

  const { table, sorting } = useDataTableControllerWithSorting(
    cols,
    list?.items || [],
    [{ id: "createdAt", desc: true }],
  );
  useEffect(() => {
    setRequest((prev) => ({
      ...prev,
      orderBy: sorting.map((s) => ({
        field: s.id,
        dsc: s.desc,
      })),
    }));
  }, [sorting]);

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <div className="flex w-full flex-col space-y-2">
      <div className="flex justify-between space-x-2">
        <div className="flex space-x-2">
          <StateFilter
            filter={request.state!}
            onChange={(value) => setRequest({ ...request, state: value })}
          />
          <ConditionFilter
            filter={request.condition!}
            onChange={(value) => setRequest({ ...request, condition: value })}
          />
          <ResetFilters onClick={() => setRequest(defaultRequest())} />
        </div>
        <div className="flex flex-row space-x-2">
          <ColumnSelection table={table} />
          {canAccessCreate && <CreateDeliveryModal />}
        </div>
      </div>
      <DataTableContainer>
        <DataTable
          table={table}
          columns={cols}
          isLoading={isLoading || isFetching}
        />
      </DataTableContainer>
      <div className="flex flex-row justify-end">
        {list && (
          <PaginationLimitOffset
            offset={request.offset!}
            limit={request.limit!}
            totalCount={list.count}
            setLimitOffset={(limit, offset) =>
              setRequest({ ...request, limit, offset })
            }
          />
        )}
      </div>
    </div>
  );
}

const defaultRequest = (): ListRequest => ({
  ownerId: {
    active: true,
    mode: FilterMode.IsNull,
    values: [],
  },
  ownerType: {
    active: true,
    mode: FilterMode.IsNull,
    values: [],
  },
  state: {
    active: true,
    values: [State.Announced],
  },
  condition: {
    active: true,
    values: [],
  },
  orderBy: [
    {
      field: "createdAt",
      dsc: true,
    },
  ],
  limit: 10,
  offset: 0,
});
